import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useForm } from '../contexts/FormContext';
import { PhoneIcon, EnvelopeIcon, MapPinIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import BookingForm from '../components/BookingForm';
import Seo from '../components/Seo';

const ContactPage: React.FC = () => {
  const { translations } = useLanguage();
  const { formContainerRef, focusAndCenterForm } = useForm();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const isBookingFlow = searchParams.has('booking');

  useEffect(() => {
    if (isBookingFlow) {
      focusAndCenterForm();
    }
  }, [focusAndCenterForm, location, isBookingFlow]);

  return (
    <main className="pt-20 lg:pt-32 pb-16">
      <Seo pageKey="contact" />
      {/* Page title */}
      <section className="bg-navy text-white py-12">
        <div className="container-custom">
          <h1 className="text-4xl font-bold mb-2 text-center">{translations.contact.title}</h1>
          <p className="text-center text-xl">{translations.contact.subtitle}</p>
        </div>
      </section>

      {/* Booking Wizard or Contact Form */}
      {isBookingFlow ? (
        <section className="py-12 bg-cream/30">
          <div className="container-custom">
            <BookingForm />
          </div>
        </section>
      ) : (
        <section className="py-16">
          <div className="container-custom">
            <div className="grid md:grid-cols-2 gap-12">
              {/* Contact information */}
              <div>
                <h2 className="text-2xl font-bold text-navy mb-6">{translations.contact.contactInfo}</h2>

                <div className="space-y-6">
                  <div className="flex items-start">
                    <PhoneIcon className="w-6 h-6 text-mint mr-3 flex-shrink-0" />
                    <div>
                      <h3 className="text-lg font-semibold text-navy">{translations.contact.phone}</h3>
                      <p className="text-gray-700">
                        <a href="tel:+34603319648" className="hover:text-mint transition-colors font-medium">
                          +34 603 31 96 48
                        </a>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <MapPinIcon className="w-6 h-6 text-mint mr-3 flex-shrink-0" />
                    <div>
                      <h3 className="text-lg font-semibold text-navy">{translations.contact.address}</h3>
                      <p className="text-gray-700">
                        Avenida de Jaime III, 3<br />
                        07012 Palma<br />
                        Illes Balears, España
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <EnvelopeIcon className="w-6 h-6 text-mint mr-3 flex-shrink-0" />
                    <div>
                      <h3 className="text-lg font-semibold text-navy">{translations.contact.email}</h3>
                      <p className="text-gray-700">
                        <a href="mailto:info@seadent.es" className="hover:text-mint transition-colors font-medium">
                          info@seadent.es
                        </a>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Emergency notice */}
                <div className="mt-12 bg-coral/5 border border-coral/20 rounded-xl p-6 shadow-sm">
                  <div className="flex items-start">
                    <ExclamationCircleIcon className="w-6 h-6 text-coral mr-3 flex-shrink-0" />
                    <div>
                      <h3 className="text-lg font-semibold text-navy">{translations.contact.emergencyTitle}</h3>
                      <p className="text-gray-700 mb-4">{translations.contact.emergencyText}:</p>
                      <a
                        href="tel:+34603319648"
                        className="inline-flex items-center justify-center bg-coral text-white hover:bg-coral-dark py-2 px-6 rounded-lg transition-colors font-bold shadow-md hover:shadow-lg"
                      >
                        <PhoneIcon className="w-5 h-5 mr-2" />
                        +34 603 31 96 48
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Legacy Contact Form replaced with Call to Action to use Booking Wizard */}
              <div className="flex flex-col justify-center items-center bg-white p-10 rounded-2xl shadow-premium border border-gray-100 text-center animate-fadeIn">
                <h2 className="text-2xl font-bold text-navy mb-4 font-title">{translations.contact.appointment}</h2>
                <p className="text-gray-600 mb-8 max-w-sm">{translations.contact.subtitle}</p>
                <a
                  href="/kontakt?booking=true"
                  className="bg-navy text-white px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:bg-navy-dark hover:shadow-xl hover:-translate-y-1 transition-all duration-300 w-full md:w-auto"
                >
                  {translations.hero.cta}
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Map section */}
      <section className="py-12 bg-gray-50">
        <div className="container-custom">
          <h2 className="text-2xl font-bold text-navy mb-8 text-center">{translations.contact.findUsTitle}</h2>

          <div className="relative h-96 rounded-lg overflow-hidden shadow-lg mb-6">
            <iframe
              title="SeaDent Mallorca Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3075.651!2d2.6437627!3d39.571492!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1297925ae0978b45%3A0x39247b6f6334df60!2sSeaDent!5e0!3m2!1sen!2ses!4v1652786526784!5m2!1sen!2ses"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen={true}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>

          <div className="text-center">
            <a
              href="https://www.google.com/maps/search/?api=1&query=Av.+de+Jaume+III,+3+Palma,+Seadent"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-navy text-white font-semibold rounded-md hover:bg-light-blue transition-colors focus:outline-none focus:ring-2 focus:ring-navy focus:ring-offset-2"
            >
              <MapPinIcon className="w-5 h-5 mr-2" />
              {translations.contact.directionsButton}
            </a>
          </div>
        </div>
      </section>

      {/* Transportation information */}
      <section className="py-12 bg-white">
        <div className="container-custom">
          <h2 className="text-2xl font-bold text-navy mb-8 text-center">{translations.contact.transportTitle}</h2>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Public Transportation */}
            <div className="bg-blue-50 p-6 rounded-lg shadow">
              <h3 className="text-xl font-semibold text-navy mb-4">{translations.contact.publicTransport}</h3>
              <p className="text-gray-700">{translations.contact.publicTransportInfo}</p>
            </div>

            {/* By Car */}
            <div className="bg-blue-50 p-6 rounded-lg shadow">
              <h3 className="text-xl font-semibold text-navy mb-4">{translations.contact.byCar}</h3>
              <p className="text-gray-700">{translations.contact.byCarInfo}</p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
